import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { KBComment } from '../../types';
import { knowledgeBaseAPI } from '../../api/services';

interface Props {
  docId: string;
}

function timeAgo(date: string): string {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface CommentItemProps {
  comment: KBComment;
  docId: string;
  onDeleted: (id: string) => void;
  currentUserId: string;
  depth?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, docId, onDeleted, currentUserId, depth = 0 }) => {
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replies, setReplies] = useState<KBComment[]>([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const loadReplies = async () => {
    if (expanded) { setExpanded(false); return; }
    setLoadingReplies(true);
    try {
      const data = await knowledgeBaseAPI.getComments(docId);
      setReplies(data.filter((c: KBComment) => c.parentCommentId === comment._id));
      setExpanded(true);
    } catch { /* ignore */ }
    finally { setLoadingReplies(false); }
  };

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      const newReply = await knowledgeBaseAPI.addComment(docId, replyText.trim(), comment._id);
      setReplies((prev) => [...prev, newReply]);
      setExpanded(true);
      setReplyText('');
      setShowReply(false);
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await knowledgeBaseAPI.deleteComment(docId, comment._id);
      onDeleted(comment._id);
    } catch { /* ignore */ }
  };

  return (
    <div className={`${depth > 0 ? 'ml-8 border-l-2 border-gray-100 pl-4' : ''}`}>
      <div className="flex gap-3 py-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {comment.author?.name?.charAt(0).toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-800">{comment.author?.name}</span>
            <span className="text-xs text-gray-400">{timeAgo(comment.createdAt)}</span>
            {comment.edited && <span className="text-xs text-gray-400 italic">(edited)</span>}
          </div>
          <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{comment.content}</p>
          <div className="flex items-center gap-3 mt-1.5">
            {depth === 0 && (
              <button
                onClick={() => setShowReply((v) => !v)}
                className="text-xs text-gray-400 hover:text-blue-600 font-medium"
              >
                Reply
              </button>
            )}
            {depth === 0 && comment.replyCount > 0 && (
              <button
                onClick={loadReplies}
                className="text-xs text-blue-500 hover:text-blue-700 font-medium"
              >
                {loadingReplies ? 'Loading…' : expanded ? 'Hide replies' : `${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
              </button>
            )}
            {comment.author?.userId === currentUserId && (
              <button onClick={handleDelete} className="text-xs text-gray-300 hover:text-red-500">Delete</button>
            )}
          </div>

          {showReply && (
            <div className="mt-2 flex gap-2">
              <textarea
                autoFocus
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply…"
                rows={2}
                className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-blue-400 resize-none"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitReply(); if (e.key === 'Escape') setShowReply(false); }}
              />
              <div className="flex flex-col gap-1">
                <button
                  onClick={submitReply}
                  disabled={submitting || !replyText.trim()}
                  className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                >
                  {submitting ? '…' : 'Reply'}
                </button>
                <button onClick={() => setShowReply(false)} className="text-xs text-gray-400 hover:text-gray-600 px-3 py-1.5">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {expanded && replies.map((r) => (
        <CommentItem
          key={r._id}
          comment={r}
          docId={docId}
          onDeleted={(id) => setReplies((prev) => prev.filter((x) => x._id !== id))}
          currentUserId={currentUserId}
          depth={depth + 1}
        />
      ))}
    </div>
  );
};

const KBCommentSection: React.FC<Props> = ({ docId }) => {
  const { user } = useSelector((s: RootState) => s.auth);
  const [comments, setComments] = useState<KBComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    knowledgeBaseAPI.getComments(docId)
      .then((data) => { if (mounted) setComments(data.filter((c: KBComment) => !c.parentCommentId)); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [docId]);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const c = await knowledgeBaseAPI.addComment(docId, text.trim());
      setComments((prev) => [c, ...prev]);
      setText('');
    } catch { /* ignore */ }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-base font-semibold text-gray-800">Comments</h3>

      {/* Composer */}
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
          {user?.name?.charAt(0).toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 space-y-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write a comment… (Ctrl+Enter to submit)"
            rows={3}
            className="w-full text-sm border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-blue-400 resize-none"
            onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}
          />
          <button
            onClick={submit}
            disabled={submitting || !text.trim()}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg disabled:opacity-50 hover:bg-blue-700 transition-colors"
          >
            {submitting ? 'Posting…' : 'Comment'}
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="py-8 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No comments yet. Be the first!</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {comments.map((c) => (
            <CommentItem
              key={c._id}
              comment={c}
              docId={docId}
              onDeleted={(id) => setComments((prev) => prev.filter((x) => x._id !== id))}
              currentUserId={user?._id ?? ''}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default KBCommentSection;
